export const formattedFilter = filterType =>
  filterType
    .toLowerCase()
    .split(' ')
    .map((s, i) => (i === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1)))
    .join('');

export const disableResetButton = activeFilters => {
  const activeFiltersArray = Object.values(activeFilters);
  const active = activeFiltersArray.some(filter => filter.length > 0);
  return !active;
};

export const sortByDate = (posts, order) => {
  return posts.sort((a, b) => {
    const dateA = new Date(a.publishDate);
    const dateB = new Date(b.publishDate);
    return order === 'Oldest' ? dateA - dateB : dateB - dateA;
  });
};

/**
 * Generates filters for blog categories based on a list of posts.
 *
 * @param {Object[]} posts - Array of blog posts.
 * @returns {Object[]} Array of filter objects.
 */
const createBlogCategoryFilters = posts => {
  const filtersMap = new Map([
    ['categories', { title: 'Categories', categories: new Set() }],
    ['topics', { title: 'Topics', categories: new Set() }],
    ['audiences', { title: 'Audiences', categories: new Set() }],
  ]);

  posts.forEach(post => {
    const postCategories = post.categories;
    const postTopics = post.topics;
    const postAudiences = post.audiences;

    postCategories?.forEach(category => {
      const filter = filtersMap.get('categories');
      if (filter) {
        filter.categories.add(category.name);
      }
    });

    postTopics?.forEach(topic => {
      const filter = filtersMap.get('topics');
      if (filter) {
        filter.categories.add(topic.name);
      }
    });

    postAudiences?.forEach(audience => {
      const filter = filtersMap.get('audiences');
      if (filter) {
        filter.categories.add(audience.name);
      }
    });
  });

  for (const [key, filter] of filtersMap) {
    if (filter.categories.size === 0) {
      filtersMap.delete(key);
    }
  }

  return Array.from(filtersMap.values()).map(filter => ({
    title: filter.title,
    categories: Array.from(filter.categories),
  }));
};

/**
 * Generates filters for learning center categories based on a list of posts.
 *
 * @param {Object[]} posts - Array of learning center posts.
 * @returns {Object[]} An array of objects with titles and categories.
 */
const createLearningCenterCategoryFilters = posts => {
  const filtersMap = new Map([
    ['role', { title: 'Role', categories: new Set() }],
    ['industry', { title: 'Industry', categories: new Set() }],
    ['topic', { title: 'Topic', categories: new Set() }],
  ]);

  posts.forEach(post => {
    post.categories.forEach(category => {
      const filter = filtersMap.get(category.categoryType);
      if (filter) {
        filter.categories.add(category.name);
      }
    });
  });

  return Array.from(filtersMap.values()).map(filter => ({
    title: filter.title,
    categories: Array.from(filter.categories),
  }));
};

/**
 * Generates filters for checklist categories based on a list of posts.
 *
 * @param {Object[]} posts - Array of checklist posts.
 * @returns {Object[]} An array of objects with titles and categories.
 */
const createChecklistCategoryFilters = posts => {
  const alphabetizeCategories = categories => {
    return categories.sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  };

  const assetTypes = [
    ...new Set(posts.map(post => post.asset.map(asset => asset?.name)).flat()),
  ];

  const industries = [...new Set(posts.map(post => post?.industry?.name))];

  const categories = [
    ...new Set(posts.map(post => post?.category?.name)),
  ].filter(el => el != null);

  return [
    {
      title: 'Asset Type',
      categories: alphabetizeCategories(assetTypes),
    },
    {
      title: 'Industry',
      categories: alphabetizeCategories(industries),
    },
    {
      title: 'Category',
      categories: alphabetizeCategories(categories),
    },
  ];
};

/**
 * Generates filters for partnership categories based on a list of posts.
 *
 * @param {Object[]} posts - Array of partnership posts.
 * @returns {Object[]} An array of objects with titles and categories.
 */
const createPatnershipCategoryFilters = posts => {
  const categories = [
    ...new Set(posts?.map(post => post?.category?.internalName)),
  ];

  return [
    {
      title: 'Category',
      categories,
    },
  ];
};

/**
 * Generates filters for events categories and types based on a list of posts.
 *
 * @param {Object[]} posts - Array of event posts.
 * @returns {Object[]} An array of objects with event types and categories.
 */
const createEventCategoryFilters = posts => {
  const categories = [
    ...new Set(posts?.map(post => post?.category?.internalName)),
  ];

  const eventTypes = [
    ...new Set(posts?.map(post => post?.eventType?.internalName)),
  ];

  return [
    {
      title: 'Type',
      categories: eventTypes,
    },
    {
      title: 'Category',
      categories,
    },
  ];
};

const createPressCategoryFilters = posts => {
  const categories = [...new Set(posts?.map(post => post?.date))];
  categories.sort((a, b) => b - a);

  return [
    {
      title: 'Category',
      categories,
    },
  ];
};

/**
 * Creates category filters based on the type of posts.
 *
 * @param {Object[]} posts - Array of posts.
 * @param {string} type - Type of the posts ('blog' or 'learning-center').
 * @returns {Object[]} An array of filter objects.
 */
export const createCategoryFilters = (posts, type) => {
  if (type === 'blog') {
    return createBlogCategoryFilters(posts);
  }
  if (type === 'learning') {
    return createLearningCenterCategoryFilters(posts);
  }
  if (type === 'checklists') {
    return createChecklistCategoryFilters(posts);
  }
  if (type === 'product-release') {
    return createBlogCategoryFilters(posts);
  }
  if (type === 'partnerships') {
    return createPatnershipCategoryFilters(posts);
  }
  if (type === 'events') {
    return createEventCategoryFilters(posts);
  }
  if (type === 'press') {
    return createPressCategoryFilters(posts);
  }
};

/**
 * Filters learning center posts based on active filters.
 *
 * @param {Object[]} posts - Array of learning center posts.
 * @param {Object} activeFilters - Active filters.
 * @returns {Object[]} Filtered array of posts.
 */
export const filterLearningCenterPosts = (posts, activeFilters) => {
  return posts.filter(post => {
    const postCategories = post.categories.map(cat => cat.name);
    return Object.entries(activeFilters).every(([, filterValues]) => {
      if (filterValues.length === 0) {
        return true;
      }
      return filterValues.some(filterValue =>
        postCategories.includes(filterValue)
      );
    });
  });
};

/**
 * Filters blog posts based on active filters.
 * This filter is also used for product release posts.
 * If changes are made to this function, ensure that that this
 * is dupplacted and used in the product release filter.
 *
 * @param {Object[]} posts - Array of blog posts.
 * @param {Object} activeFilters - Active filters.
 * @returns {Object[]} Filtered array of posts.
 */
export const filterBlogPosts = (posts, activeFilters) => {
  return posts.filter(post => {
    const postCategories = post?.categories?.map(cat => cat.name);
    const postTopics = post?.topics?.map(topic => topic.name);
    const postAudiences = post?.audiences?.map(audience => audience.name);

    return Object.entries(activeFilters).every(([, filterValues]) => {
      if (filterValues.length === 0) {
        return true;
      }
      return filterValues.some(
        filterValue =>
          postCategories.includes(filterValue) ||
          postTopics.includes(filterValue) ||
          postAudiences.includes(filterValue)
      );
    });
  });
};

/**
 * Filters templates based on active filters.
 *
 * @param {Object[]} posts - Array of templates.
 * @param {Object} activeFilters - Active filters.
 * @returns {Object[]} Filtered array of templates.
 */

export const filterChecklistPosts = (posts, activeFilters) => {
  const filterByAssetType = (post, assetTypes) => {
    const postAssetTypes = post.asset.map(asset => asset.name);
    return (
      assetTypes.length === 0 ||
      assetTypes.some(assetType => postAssetTypes.includes(assetType))
    );
  };

  const filterByIndustry = (post, industries) => {
    return (
      industries.length === 0 ||
      industries.some(industry => post.industry.name === industry)
    );
  };

  const filterByCategory = (post, categories) => {
    return categories.length === 0 || categories.includes(post.category?.name);
  };

  return posts.filter(post => {
    const assetTypes = activeFilters.assetType || [];
    const industries = activeFilters.industry || [];
    const categories = activeFilters.category || [];

    return (
      filterByAssetType(post, assetTypes) &&
      filterByIndustry(post, industries) &&
      filterByCategory(post, categories)
    );
  });
};

/**
 * Toggles a filter on or off in the active filters list.
 *
 * @param {Function} setActiveFilters - Function to update the active filters.
 * @param {string} filterType - The type of the filter to toggle.
 * @param {string} category - The category to toggle.
 */
export const toggleFilter = (setActiveFilters, filterType, category) => {
  const filter = formattedFilter(filterType);
  setActiveFilters(prevFilters => {
    const currentFilters = prevFilters[filter] || [];
    const isAlreadyActive = currentFilters.includes(category);
    const updatedFilters = isAlreadyActive
      ? currentFilters.filter(cat => cat !== category)
      : [...currentFilters, category];

    return {
      ...prevFilters,
      [filter]: updatedFilters,
    };
  });
};

/**
 * Filters partnership posts based on active filters.
 *
 * @param {Object[]} posts - Array of blog posts.
 * @param {Object} activeFilters - Active filters.
 * @returns {Object[]} Filtered array of posts.
 */
export const filterPartnershipPosts = (posts, activeFilters) => {
  return posts.filter(post => {
    const postCategories = [post.category?.internalName];
    return Object.entries(activeFilters).every(([, filterValues]) => {
      if (filterValues.length === 0) {
        return true;
      }
      return filterValues.some(filterValue =>
        postCategories.includes(filterValue)
      );
    });
  });
};

/**
 * Filters partnership posts based on active filters.
 *
 * @param {Object[]} posts - Array of event posts.
 * @param {Object} activeFilters - Active filters.
 * @returns {Object[]} Filtered array of posts.
 */
export const filterEvents = (posts, activeFilters) => {
  if (!activeFilters.category.length && !activeFilters.type.length) {
    return posts;
  }
  return posts.filter(post => {
    const categories = activeFilters.category;
    const eventTypes = activeFilters.type;

    const matchesCategory =
      categories.length === 0 ||
      categories.includes(post.category.internalName);

    const matchesEventType =
      eventTypes.length === 0 ||
      eventTypes.includes(post?.eventType?.internalName);

    return matchesCategory && matchesEventType;
  });
};

/**
 * Filters press posts based on active filters.
 *
 * @param {Object[]} posts - Array of press posts.
 * @param {Object} activeFilters - Active filters.
 * @returns {Object[]} Filtered array of posts.
 */
export const filterPressPosts = (posts, activeFilters) => {
  return posts.filter(post => {
    const postDate = post.date;
    return (
      activeFilters.category.length === 0 ||
      activeFilters.category.includes(postDate)
    );
  });
};
